import { useState } from "react";
import { Link } from "react-router-dom";
import SiteBreadcrumbs from "@/components/feature/Breadcrumbs";
import { faqItems } from "@/mocks/faq";
import { useSeo } from "@/hooks/useSeo";

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useSeo({
    title: "常见问题 - 内容来源与纠错说明 | 瓜田秘闻社",
    description:
      "关于瓜田秘闻社你最想知道的几件事：内容从哪来、为什么用匿名代称、如何纠错投稿，以及更新频率与内容原则，都在这里一次说清。",
    keywords: "黑料吃瓜常见问题,内容原则,纠错投稿,圈内八卦",
    canonical: "/faq",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  });

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <SiteBreadcrumbs items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            常见问题
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            关于内容来源、匿名代称与纠错方式，这里集中回答读者问得最多的几个问题。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <aside className="lg:col-span-4">
            <div className="rounded-lg bg-secondary-100 p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                <i className="ri-question-answer-line text-xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                没找到想要的答案？
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                直接把问题发给我们，工作日一般当天就会回复；线索类投稿我们会单独核实跟进。
              </p>
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-line"></i> 去留言
              </Link>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <dl className="flex flex-col gap-3">
              {faqItems.map((item, index) => {
                const open = openIndex === index;
                return (
                  <div
                    key={item.question}
                    className={`overflow-hidden rounded-lg border bg-background-50 transition-colors ${
                      open ? "border-primary-300" : "border-background-200"
                    }`}
                  >
                    <dt>
                      <button
                        type="button"
                        onClick={() => setOpenIndex(open ? null : index)}
                        aria-expanded={open}
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      >
                        <span className="text-sm font-medium text-foreground-900 md:text-base">
                          {item.question}
                        </span>
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                            open ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600"
                          }`}
                        >
                          <i className={open ? "ri-subtract-line" : "ri-add-line"}></i>
                        </span>
                      </button>
                    </dt>
                    <dd className={`px-5 pb-4 ${open ? "block" : "hidden"}`}>
                      <p className="border-t border-background-200 pt-3 text-sm leading-relaxed text-foreground-600">
                        {item.answer}
                      </p>
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}